/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
/* eslint react-hooks/refs: ["warn"] */
import React from "react";
import { Trans } from "@coworker/locales";
import taskStateOptions from "@coworker/enums/taskState";
import { useInputPopup } from "../../components/InputPopup";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useWorkspacesState } from "../../hooks/useWorkspaces";
import activityTypes from "@coworker/enums/activityTypes";
import trackerHelper from "../../helpers/tracker";
import useUndoneNotification from "../../components/useUndoneNotification";
import { useCloseTask } from "./useCloseTask";
import { useStoreId, useUID } from "../../core/auth/useLoggedInUser";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateTaskMutation } from "@coworker/apprestructured/src/tasks/hooks/useUpdateTaskById";
import { useCreateNote } from "../useCreateNote";
import { httpClient } from "@coworker/http-client";
import {
  COMPLETED_TASKS_QUERY_KEY,
  IN_PROGRESS_TASKS_QUERY_KEY,
  OPEN_TASKS_QUERY_KEY,
  OVERVIEW_TASKS_QUERY_KEY,
} from "@coworker/apprestructured/src/TasksOverview/constants/queryKeys";

export const useUndoCompleteWithChanges = ({
  task,
  uid,
  taskId,
  afterUndo,
}) => {
  const undoCurtain = useUndoneNotification();
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  const undoCompleteWithChanges = React.useCallback(
    async ({ created_note_id, action_id }) => {
      const data = {
        picked: task.picked || false,
        pickup_location: task.pickup_location,
        pickup_location_custom: task.pickup_location_custom,
        pickup_location_grid_code: task.pickup_location_grid_code,
        pickup_location_department: task.pickup_location_department,
        pick_quantity: task.picked ? task.pick_quantity : null,
        actual_pick_quantity: task.picked ? task.actual_pick_quantity : null,
        pick_type: task.picked ? task.pick_type : "",
        refilled_quantity: null,
        actual_refilled_quantity: null,
        refilled_type: "",
        last_editor_id: uid,
        assigned_user_id: task.assigned_user_id,
        assigned_team_id: task.assigned_team_id,
        state: task.state,
        undo_action_id: action_id || "",
        activity_type: activityTypes.UNDO_COMPLETE,
        task_type_detail: task.picked ? "REFILL" : "PICK_AND_REFILL",
        searchable_location:
          task.pickup_location_custom ?? task.pickup_location,
      };

      await undoCurtain(data, taskId);

      await queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, COMPLETED_TASKS_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
      });

      if (created_note_id) {
        await httpClient.tasks.delete(`/v1/task/notes/${created_note_id}`);
      }
      afterUndo();
    },
    [undoCurtain, task, uid, taskId, afterUndo, storeId, queryClient]
  );

  return {
    call: undoCompleteWithChanges,
  };
};

export const useCompleteWithChanges = (preparedForm, taskId) => {
  const { navigationState } = useWorkspacesState();
  const { showToastWithUndo } = useToastNotification();
  const date = new Date().getTime();
  const actionIdComplete = `complete-${date}`;
  const actionIdPicked = `picked-${date}`;
  const { getInput } = useInputPopup();
  const uid = useUID();
  const { toggleModal } = React.useContext(ModalContext);
  const queryClient = useQueryClient();
  const updateTask = useUpdateTaskMutation();
  const createNote = useCreateNote(taskId);
  const taskBeforeChanging = React.useRef(preparedForm);
  const { call: undoCompleteWithChanges } = useUndoCompleteWithChanges({
    task: taskBeforeChanging.current,
    uid,
    taskId,
    afterUndo: () =>
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: taskId, taskType: preparedForm?.task_type },
      }),
  });

  const { call: closeTask } = useCloseTask(preparedForm, taskId, {
    undo: true,
    afterUndo: () => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId: taskId, taskType: preparedForm?.task_type },
      });
    },
  });

  const completeWithChanges = React.useCallback(async () => {
    const {
      pick_quantity,
      actual_pick_quantity,
      pick_type,
      refilled_quantity,
      actual_refilled_quantity,
      refilled_type,
      note,
      pickup_location,
      pickup_location_custom,
      pickup_location_grid_code,
      pickup_location_department,
      undo_action_id = "",
      location,
    } = preparedForm;

    const taskUpdate = {
      pick_quantity,
      actual_pick_quantity,
      pick_type,
      pickup_location,
      pickup_location_custom,
      pickup_location_grid_code,
      pickup_location_department,
      refilled_quantity,
      actual_refilled_quantity,
      refilled_type,
      last_editor_id: uid,
      action_id: actionIdComplete,
      activity_type: activityTypes.COMPLETE,
      undo_action_id,
      task_type_detail: "REFILL",
      searchable_location: location,
    };

    async function addNote() {
      if (note) {
        return await createNote.mutateAsync({
          data: { text: note, creator_id: uid },
          images: [],
        });
      }
    }

    async function updatePickingTask(taskUpdate) {
      if (pick_quantity === 0) {
        return closeTask();
      }
      const handoff = await getInput("handoff", {});
      if (!handoff) {
        return;
      }
      const { uid: handoffUserId, gid: handoffGroupId } = handoff;
      taskUpdate.state = !handoffUserId
        ? taskStateOptions.UNASSIGNED
        : taskStateOptions.ASSIGNED;
      taskUpdate.assigned_user_id = handoffUserId || "";
      taskUpdate.assigned_team_id = handoffGroupId;
      taskUpdate.picked = true;
      taskUpdate.action_id = actionIdPicked;
      taskUpdate.activity_type = activityTypes.PICKED;

      // The onTaskUpdate trigger also deletes undo_action_id, but in the case that this backend change has not propagated to the frontend we must make sure to also signal this from the frontend!
      taskUpdate.undo_action_id = "";

      if (
        handoffGroupId !== preparedForm.assigned_team_id ||
        (!handoffUserId && handoffGroupId === preparedForm.assigned_team_id)
      ) {
        trackerHelper.trackHandoffTask(taskId, handoffUserId, handoffGroupId);
        taskUpdate.activity_type = activityTypes.PICKED_AND_REASSIGNED_TEAM;
      } else if (
        handoffUserId &&
        handoffUserId !== preparedForm.assigned_user_id
      ) {
        trackerHelper.trackHandoffTask(taskId, handoffUserId, handoffGroupId);
        taskUpdate.activity_type = activityTypes.PICKED_AND_REASSIGNED_USER;
      }

      await updateTask.mutateAsync({ data: taskUpdate, taskId });

      trackerHelper.trackPickAddonTask(
        { ...taskUpdate, taskId, pick_quantity },
        true,
        !(
          preparedForm.pickup_location === taskUpdate.pickup_location &&
          preparedForm.pickup_location_custom ===
            taskUpdate.pickup_location_custom &&
          preparedForm.pickup_location_department ===
            taskUpdate.pickup_location_department &&
          preparedForm.pickup_location_grid_code ===
            taskUpdate.pickup_location_grid_code
        ),
        preparedForm.requested_quantity !== taskUpdate.pick_quantity
      );
      const result = await addNote();

      showToastWithUndo(
        <Trans>pickingNotification2String</Trans>,
        () =>
          undoCompleteWithChanges({
            created_note_id: result.created_note_id,
            action_id: taskUpdate.action_id,
          }),
        3
      );

      return true;
    }

    async function updateRefillTask(taskUpdate) {
      if (refilled_quantity === 0) {
        return closeTask();
      }

      taskUpdate.state = taskStateOptions.COMPLETED;
      taskUpdate.activity_type = activityTypes.COMPLETE;
      // onUpdate trigger takes care of deleting undo_action_id
      await updateTask.mutateAsync({ data: taskUpdate, taskId });

      trackerHelper.trackRefillAddonTask({ ...taskUpdate, taskId }, true);
      trackerHelper.trackCompleteTask(
        { ...taskUpdate, taskId },
        { with_changes: true, pick_and_refill: !preparedForm.picked }
      );

      const result = await addNote();
      showToastWithUndo(
        <Trans>completed2String</Trans>,
        () =>
          undoCompleteWithChanges({
            created_note_id: result.created_note_id,
            action_id: taskUpdate.action_id,
          }),
        3
      );

      return true;
    }

    await queryClient.invalidateQueries({
      queryKey: [OVERVIEW_TASKS_QUERY_KEY, COMPLETED_TASKS_QUERY_KEY],
    });
    await queryClient.invalidateQueries({
      queryKey: [OVERVIEW_TASKS_QUERY_KEY, IN_PROGRESS_TASKS_QUERY_KEY],
    });

    if (!taskUpdate.assigned_user_id) {
      await queryClient.invalidateQueries({
        queryKey: [OVERVIEW_TASKS_QUERY_KEY, OPEN_TASKS_QUERY_KEY],
      });
    }

    if (navigationState.picking) {
      return await updatePickingTask(taskUpdate);
    } else {
      return await updateRefillTask(taskUpdate);
    }
  }, [
    updateTask,
    createNote,
    preparedForm,
    uid,
    actionIdComplete,
    navigationState.picking,
    taskId,
    getInput,
    actionIdPicked,
    showToastWithUndo,
    closeTask,
    undoCompleteWithChanges,
    queryClient,
  ]);

  return { call: completeWithChanges, updateTaskMutation: updateTask };
};
